import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, ROLE_LABELS } from "@/lib/api";
import { toast } from "sonner";
import { Bug, ArrowClockwise, X, MagnifyingGlass, CheckCircle, Clock, ArrowSquareOut } from "@phosphor-icons/react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

/**
 * Iter208.6 — Admin: sezione Feedback Beta.
 * Vista cronologica di tutti i feedback beta ricevuti dagli utenti.
 * Filtri: stato (Nuovo / In gestione / Risolto). Iter209dze — rimosso
 * il tab "Tutti" e impostato "Nuovo" come default all'apertura della pagina.
 * Colonne: data, utente, ruolo, pagina, testo, screenshot (cliccabile), stato (select modificabile).
 */
const STATUS_LABELS = {
  new: "Nuovo",
  in_progress: "In gestione",
  resolved: "Risolto",
};
const STATUS_STYLES = {
  new: "bg-amber-100 text-amber-800 border-amber-200",
  in_progress: "bg-blue-100 text-blue-800 border-blue-200",
  resolved: "bg-emerald-100 text-emerald-800 border-emerald-200",
};
// Iter209au — ROLE_LABELS ora importato da @/lib/api (single source).

function fmtDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function AdminFeedback() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ all: 0, new: 0, in_progress: 0, resolved: 0 });
  const [statusFilter, setStatusFilter] = useState("new");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // id in fase di update
  const [preview, setPreview] = useState(null); // url screenshot ingrandito

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      params.set("limit", "200");
      const r = await api.get(`/admin/feedback?${params.toString()}`);
      setItems(r.data?.items || []);
      setCounts(r.data?.counts || { all: 0, new: 0, in_progress: 0, resolved: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel caricamento");
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Iter209do — Al mount della pagina Feedback forza refresh del badge
  // sidebar: evita latenza fino al prossimo poll (60s) se un feedback è
  // arrivato mentre l'admin era su un'altra schermata.
  useEffect(() => {
    try { window.dispatchEvent(new Event("feedback:refresh")); } catch (_) {}
  }, []);

  const filtered = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return items;
    return items.filter((x) => {
      const hay = [x.user_name, x.user_email, x.user_role, x.message, x.page_url, x.page_title]
        .map((s) => (s || "").toString().toLowerCase()).join(" ");
      return hay.includes(q);
    });
  }, [items, search]);

  const updateStatus = async (fid, next) => {
    setSaving(fid);
    try {
      await api.patch(`/admin/feedback/${fid}/status`, { status: next });
      toast.success(`Stato aggiornato: ${STATUS_LABELS[next]}`);
      setItems((prev) => prev.map((x) => (x.id === fid ? { ...x, status: next } : x)));
      // aggiorna contatori localmente
      setCounts((c) => ({ ...c })); // reload from server for consistency
      load();
      // Iter209do — notifica alla sidebar di rifare il count del badge
      // "Feedback Beta": se un item esce da `new` il numero rosso cala subito.
      try { window.dispatchEvent(new Event("feedback:refresh")); } catch (_) {}
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setSaving(null); }
  };

  return (
    <div className="space-y-6" data-testid="admin-feedback-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Admin</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 flex items-center gap-3 mt-1">
            <Bug size={28} weight="bold" className="text-purple-600" />
            Feedback Beta
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Segnalazioni e suggerimenti degli utenti in ordine cronologico.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded border border-slate-300 bg-white hover:bg-slate-50 text-sm font-semibold text-slate-700"
          data-testid="admin-feedback-refresh"
        >
          <ArrowClockwise size={14} weight="bold" /> Aggiorna
        </button>
      </header>

      {/* Filtri stato — Iter209dze: rimosso "Tutti", default "Nuovo". */}
      <div className="flex flex-wrap items-center gap-2" data-testid="admin-feedback-filters">
        <FilterChip active={statusFilter === "new"} onClick={() => setStatusFilter("new")} label="Nuovo" count={counts.new} tone="amber" testid="admin-feedback-filter-new" />
        <FilterChip active={statusFilter === "in_progress"} onClick={() => setStatusFilter("in_progress")} label="In gestione" count={counts.in_progress} tone="blue" testid="admin-feedback-filter-in_progress" />
        <FilterChip active={statusFilter === "resolved"} onClick={() => setStatusFilter("resolved")} label="Risolto" count={counts.resolved} tone="emerald" testid="admin-feedback-filter-resolved" />

        <div className="relative ml-auto w-full sm:w-72">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per utente, testo, pagina…"
            className="w-full pl-8 pr-3 py-2 text-sm border rounded bg-white"
            data-testid="admin-feedback-search"
          />
        </div>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="text-sm text-slate-500 italic">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border rounded-lg p-8 text-center text-sm text-slate-500 italic" data-testid="admin-feedback-empty">
          Nessun feedback con stato "{STATUS_LABELS[statusFilter] || statusFilter}".
        </div>
      ) : (
        <div className="bg-white border rounded-lg overflow-hidden">
          {/* Header desktop */}
          <div className="hidden md:grid grid-cols-[140px_1fr_90px_1fr_1fr_60px_130px] gap-3 px-4 py-2 text-[11px] font-semibold text-slate-500 uppercase border-b bg-slate-50">
            <div>Data</div>
            <div>Utente</div>
            <div>Ruolo</div>
            <div>Pagina</div>
            <div>Testo</div>
            <div className="text-center">Shot</div>
            <div>Stato</div>
          </div>
          <ul>
            {filtered.map((f) => (
              <li
                key={f.id}
                className="border-b last:border-b-0 hover:bg-slate-50/50 transition-colors"
                data-testid={`admin-feedback-row-${f.id}`}
              >
                {/* Desktop */}
                <div className="hidden md:grid grid-cols-[140px_1fr_90px_1fr_1fr_60px_130px] gap-3 px-4 py-3 items-start text-sm">
                  <div className="text-[11px] text-slate-500 flex items-center gap-1">
                    <Clock size={11} className="shrink-0" />
                    {fmtDate(f.created_at)}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{f.user_name || "-"}</div>
                    <div className="text-[11px] text-slate-500 truncate">{f.user_email || ""}</div>
                  </div>
                  <div>
                    <span className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border">
                      {ROLE_LABELS[f.user_role] || f.user_role || "-"}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <div className="text-slate-700 truncate">{f.page_title || "-"}</div>
                    {f.page_url && (
                      <a
                        href={f.page_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[11px] text-purple-700 hover:underline truncate inline-flex items-center gap-1 max-w-full"
                        data-testid={`admin-feedback-url-${f.id}`}
                      >
                        <span className="truncate">{f.page_url}</span>
                        <ArrowSquareOut size={10} className="shrink-0" />
                      </a>
                    )}
                  </div>
                  <div className="text-slate-800 whitespace-pre-line" data-testid={`admin-feedback-msg-${f.id}`}>
                    {f.message}
                  </div>
                  <div className="text-center">
                    {f.screenshot_url ? (
                      <button
                        type="button"
                        onClick={() => setPreview(f.screenshot_url)}
                        className="inline-block border rounded overflow-hidden hover:ring-2 ring-purple-400"
                        title="Apri screenshot"
                        data-testid={`admin-feedback-shot-${f.id}`}
                      >
                        <img src={f.screenshot_url} alt="shot" className="w-10 h-10 object-cover" />
                      </button>
                    ) : (
                      <span className="text-[10px] text-slate-400">—</span>
                    )}
                  </div>
                  <div>
                    <StatusSelect
                      value={f.status || "new"}
                      onChange={(v) => updateStatus(f.id, v)}
                      disabled={saving === f.id}
                      testid={`admin-feedback-status-${f.id}`}
                    />
                  </div>
                </div>

                {/* Mobile card */}
                <div className="md:hidden p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 text-sm truncate">{f.user_name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{f.user_email}</div>
                    </div>
                    <span className={"text-[10px] font-semibold px-2 py-0.5 rounded-full border shrink-0 " + (STATUS_STYLES[f.status] || STATUS_STYLES.new)}>
                      {STATUS_LABELS[f.status] || f.status}
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1"><Clock size={11} />{fmtDate(f.created_at)}</span>
                    <span className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border">
                      {ROLE_LABELS[f.user_role] || f.user_role || "-"}
                    </span>
                  </div>
                  <div className="text-sm text-slate-800 whitespace-pre-line">{f.message}</div>
                  {f.page_url && (
                    <a
                      href={f.page_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] text-purple-700 hover:underline inline-flex items-center gap-1 truncate max-w-full"
                    >
                      <span className="truncate">{f.page_title || f.page_url}</span>
                      <ArrowSquareOut size={10} />
                    </a>
                  )}
                  <div className="flex items-center gap-2">
                    {f.screenshot_url && (
                      <button
                        type="button"
                        onClick={() => setPreview(f.screenshot_url)}
                        className="border rounded overflow-hidden hover:ring-2 ring-purple-400"
                      >
                        <img src={f.screenshot_url} alt="shot" className="w-12 h-12 object-cover" />
                      </button>
                    )}
                    <div className="flex-1">
                      <StatusSelect
                        value={f.status || "new"}
                        onChange={(v) => updateStatus(f.id, v)}
                        disabled={saving === f.id}
                        testid={`admin-feedback-status-m-${f.id}`}
                      />
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Preview screenshot */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-4xl p-2" data-testid="admin-feedback-preview">
          {preview && (
            <div className="relative">
              <img src={preview} alt="Screenshot ingrandito" className="w-full max-h-[80vh] object-contain rounded" />
              <a
                href={preview}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute bottom-2 right-2 bg-white/90 hover:bg-white text-slate-700 text-xs font-semibold px-2 py-1 rounded shadow inline-flex items-center gap-1"
              >
                Apri originale <ArrowSquareOut size={11} />
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FilterChip({ active, onClick, label, count, tone = "slate", testid }) {
  const activeCls = active
    ? "bg-purple-600 text-white border-purple-600"
    : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50";
  return (
    <button
      type="button"
      onClick={onClick}
      className={"inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors " + activeCls}
      data-testid={testid}
      aria-pressed={active}
    >
      {label}
      <span className={"text-[10px] font-bold rounded-full px-1.5 py-0.5 " + (active ? "bg-white/20" : "bg-slate-100 text-slate-600")}>
        {count ?? 0}
      </span>
    </button>
  );
}

function StatusSelect({ value, onChange, disabled, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={"w-full text-xs font-semibold border rounded px-2 py-1.5 " + (STATUS_STYLES[value] || STATUS_STYLES.new)}
      data-testid={testid}
    >
      <option value="new">Nuovo</option>
      <option value="in_progress">In gestione</option>
      <option value="resolved">Risolto</option>
    </select>
  );
}
